import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { forwardRef, SyntheticEvent } from "react";
import { KeyboardEvent } from "react";
import { ControllerRenderProps, useFormContext } from "react-hook-form";

const tagsRegex = /^[a-zA-Z0-9._]*$/;

export interface Props extends ControllerRenderProps {
  label?: string;
  placeholder?: string;
  helperText?: string;
  acceptsNewTags?: boolean;
}

export const TagsField = forwardRef(
  ({ label, placeholder, helperText = "", acceptsNewTags = true, ...props }: Props, ref) => {
    const { formState, setError, clearErrors } = useFormContext();

    const customErrorKey = props.name + "_persist";

    const clearError = () => {
      clearErrors(customErrorKey);
    };

    const validate = (e: KeyboardEvent) => {
      const values = props.value as string[];
      const typedValue = (e.target as HTMLInputElement).value;

      const isInvalid = !tagsRegex.test(typedValue);
      const isDuplicate = values.includes(typedValue);

      if (isInvalid) {
        setError(customErrorKey, {
          type: "custom",
          message: "Labels can only contain letters, numbers, periods and underscores"
        });
      } else if (isDuplicate) {
        setError(customErrorKey, {
          type: "custom",
          message: "Duplicate labels are not allowed"
        });
      } else {
        clearError();
      }
    };

    const error = formState.errors[customErrorKey]?.message as string | undefined;

    const updateValueInForm = (event: SyntheticEvent, newValue: string[]) => {
      if (!error) {
        props.onChange(newValue);
      } else {
        event.preventDefault();
      }
    };

    return (
      <Autocomplete
        {...props}
        options={[]}
        multiple
        freeSolo
        clearOnBlur
        fullWidth
        onBlur={clearError}
        onKeyUp={validate}
        onChange={updateValueInForm}
        ChipProps={{
          size: "small"
        }}
        sx={{
          "& .MuiFormControl-root": {
            mt: 0
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            helperText={
              helperText + (error ? `${helperText?.endsWith(".") ? " " : ". "}${error}.` : "")
            }
            error={!!error}
            variant="outlined"
          />
        )}
        ref={ref}
        inputValue={acceptsNewTags ? undefined : ""}
      />
    );
  }
);
